import { isBooking } from './bookingsTypeHelper';
import { isConfirmed } from './bookingsActionHelper';

export const getBookingsBaseUrl = (accountKind?: string): string => {
  if (accountKind === 'SITTER') {
    return '/provider/bookings';
  }
  return '/bookings';
};

export const getBookingsUrlWithNotification = (notification: string, accountKind?: string): string => {
  const baseUrl = getBookingsBaseUrl(accountKind);
  return `${baseUrl}?notification=${encodeURIComponent(notification)}`;
};

/**
 * Generates a URL for a contract (booking or interview) or interview request.
 *
 * Note: Contracts can only be 'accepted' or 'confirmed'. However, this function also accepts
 * 'pending' for interview requests (which are not contracts yet) to support URL generation
 * for the interview flow. Once an interview is accepted, it becomes a contract.
 *
 * @param nodeId - The contract UUID (or interview request UUID for pending interviews)
 * @param accountKind - 'SITTER' for provider, 'PARENT' for seeker, or undefined for default (parent)
 * @param contractType - 'booking' or 'interview'
 * @param status - Status: 'accepted' | 'confirmed' | 'pending'
 *   - For contracts: 'accepted' | 'confirmed'
 *   - For interview requests: 'pending' (not a contract)
 * @returns The generated URL path
 */
export const getContractUrl = (
  nodeId: string,
  accountKind?: string,
  contractType?: 'booking' | 'interview',
  status?: 'accepted' | 'confirmed' | 'pending'
): string => {
  const baseUrl = getBookingsBaseUrl(accountKind);
  if (contractType) {
    if (status) {
      return `${baseUrl}/${contractType}/${status}/${nodeId}`;
    }
    return `${baseUrl}/${contractType}/${nodeId}`;
  }
  return `${baseUrl}/${nodeId}`;
};

/**
 * Generates a URL for a booking contract.
 *
 * Booking status is determined by `scCareSession.careGiverValidationStatus`:
 * - 'accepted': contract exists but not yet confirmed (includes careGiverValidationStatus === 'pending')
 * - 'confirmed': careGiverValidationStatus === 'success' or 'rejected'
 *
 * @param nodeId - The booking contract UUID
 * @param accountKind - 'SITTER' for provider, 'PARENT' for seeker, or undefined for default (parent)
 * @param status - 'accepted' | 'confirmed'
 * @returns The generated URL path
 */
export const getBookingUrl = (nodeId: string, accountKind?: string, status?: 'accepted' | 'confirmed'): string => {
  return getContractUrl(nodeId, accountKind, 'booking', status);
};

/**
 * Generates a URL for an interview (contract or request).
 *
 * Note: A pending interview is a request, not a contract. Once accepted, it becomes a contract
 * (interviewKind "interview"). This function handles both cases for URL generation.
 *
 * @param nodeId - The interview UUID (contract or request)
 * @param accountKind - 'SITTER' for provider, 'PARENT' for seeker, or undefined for default (parent)
 * @param status - 'pending' | 'accepted'
 *   - 'pending': interview request (not yet a contract)
 *   - 'accepted': interview contract (interviewKind "interview")
 * @returns The generated URL path
 */
export const getInterviewUrl = (nodeId: string, accountKind?: string, status?: 'pending' | 'accepted'): string => {
  return getContractUrl(nodeId, accountKind, 'interview', status);
};

export const getRequestUrl = (nodeId: string): string => {
  return `/requests/${nodeId}`;
};

/**
 * Generates a URL for a contract node by automatically determining its type and status.
 * This is a convenience function that encapsulates the logic for determining contract type
 * (booking vs interview) and status (accepted vs confirmed).
 *
 * @param contract - The contract node object
 * @param accountKind - 'SITTER' for provider, 'PARENT' for seeker, or undefined for default (parent)
 * @returns The generated URL path
 */
export const getContractUrlFromNode = (contract: any, accountKind?: string): string => {
  if (!contract?.id) return '';

  const isBookingNode = isBooking(contract);
  const contractType = isBookingNode ? 'booking' : 'interview';

  let status: 'accepted' | 'confirmed';
  if (isBookingNode) {
    const confirmed = isConfirmed(contract);
    status = confirmed ? 'confirmed' : 'accepted';
  } else {
    status = 'accepted';
  }

  return getContractUrl(contract.id, accountKind, contractType, status);
};

export const getSitterJobsURL = (providerType: string): string => {
  const jobsURL = '/jobs/search';

  switch (providerType) {
    case 'BABYSITTER':
      return `${jobsURL}?search_strategy=babysitter_job`;
    case 'NANNY':
      return `${jobsURL}?search_strategy=nanny_job`;
    case 'SPECIAL_NEEDS':
      return `${jobsURL}?search_strategy=special_needs_job`;
    case 'COMPANION_CARE':
      return `${jobsURL}?search_strategy=companion_care_job`;
    case 'PET_CARE':
      return `${jobsURL}?search_strategy=pet_care_job`;
    case 'HOUSEKEEPING':
      return `${jobsURL}?search_strategy=housekeeping_job`;
    default:
      return jobsURL;
  }
};

export const getParentJobsURL = (careType: string, zipcode: string): string => {
  const newSearchURL = '/availability/search';
  const oldSearchURL = '/profile/provider/search?search_strategy=';
  const zipcodeParam = zipcode ? `&zipcode=${zipcode}` : '';

  switch (careType) {
    case 'BABYSITTER':
      return `${newSearchURL}?careType=babysitter${zipcodeParam}`;
    case 'NANNY':
      return `${newSearchURL}?careType=nanny${zipcodeParam}`;
    case 'SPECIAL_NEEDS':
      return `${oldSearchURL}special_needs${zipcodeParam}`;
    case 'COMPANION_CARE':
      return `${oldSearchURL}companion_care${zipcodeParam}`;
    case 'PET_CARE':
      return `${oldSearchURL}pet_care${zipcodeParam}`;
    case 'HOUSEKEEPING':
      return `${oldSearchURL}housekeeping${zipcodeParam}`;
    default:
      return newSearchURL;
  }
};
